import { useCallback, useEffect, useState } from "react";
import { fetchMyPlans, cancelSubscription, setManualSubscriptionPause } from "./nativeProfile";
import { InlineLoader } from "./PageLoader";
import EmptyState from "./EmptyState";
import { formatPlanDate, isManualPlan, planAccessUntil, planEnded, planRenewUrl, planStatusLabel, planTrialEndsAt } from "./subscriptionGate";

/**
 * "My Plans" modal — the member's purchased plans/subscriptions from
 * api/get-myplans (product, price, payment type, date, invoice id). Read-only;
 * checkout/billing itself lives in the external FlexiFunnels system.
 */

/** Lighten/darken a hex colour — same local helper idiom as ProfileModal. */
function shade(hex, percent) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const amt = Math.round(2.55 * percent);
  const r = clamp((num >> 16) + amt);
  const g = clamp(((num >> 8) & 0x00ff) + amt);
  const b = clamp((num & 0x0000ff) + amt);
  return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`;
}

/* Small inline icons — kept local so the modal carries no new dependency. */
const Icon = {
  wallet: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.5A2.5 2.5 0 015.5 6H18a2 2 0 012 2v1M3 8.5V17a2 2 0 002 2h14a2 2 0 002-2v-3M3 8.5h13.5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.5h-3.25a1.75 1.75 0 100 3.5H21z" />
    </svg>
  ),
  calendar: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 3v3m8-3v3M4 9h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
    </svg>
  ),
  receipt: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 3v18l2-1.2L10 21l2-1.2L14 21l2-1.2L18 21V3H6zM9 8h6M9 12h6" />
    </svg>
  ),
  renew: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 11A8 8 0 006.3 6.3L4 8.5M4 4v4.5h4.5M4 13a8 8 0 0013.7 4.7L20 15.5M20 20v-4.5h-4.5" />
    </svg>
  ),
  clock: (cls) => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5V12l3 1.8" />
    </svg>
  ),
};

// `embedded` renders the list as a page card (Spotlight's My Plans page) instead
// of a popup: no overlay, no close button, no height cap.
export default function MyPlansModal({ member, primary = "#0274F7", onClose, embedded = false }) {
  const [plans, setPlans] = useState(null); // null = loading
  const [error, setError] = useState(false);
  // Cancel flow: which plan is being cancelled, why, and the in-flight state.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState(null); // { tone, text }
  const [pauseTarget, setPauseTarget] = useState(null);
  const [resumeAt, setResumeAt] = useState("");
  const [pausing, setPausing] = useState(false);

  const load = useCallback(
    (signal, force = false) =>
      fetchMyPlans({
        funnelId: member?.funnel_id,
        memberId: member?.community_member_id,
        jwt: member?.member_token,
        force,
      })
        .then((p) => { if (!signal?.off) setPlans(p); })
        .catch(() => { if (!signal?.off) setError(true); }),
    [member?.community_member_id, member?.funnel_id, member?.member_token],
  );

  useEffect(() => {
    const signal = { off: false };
    load(signal);
    return () => { signal.off = true; };
  }, [load]);

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setNotice(null);
    try {
      const res = await cancelSubscription({
        memberId: member?.community_member_id,
        memberEmail: member?.email,
        productId: cancelTarget.product_id,
        subscriptionId: cancelTarget.subscription_id,
        reason: cancelReason,
        jwt: member?.member_token,
      });
      if (res.success) {
        setNotice({ tone: "ok", text: res.message || "Subscription cancelled successfully." });
        setCancelTarget(null);
        setCancelReason("");
        await load(undefined, true); // reflect the new status, past the cache
      } else {
        setNotice({
          tone: "err",
          text: res.message || "We could not cancel this subscription. Please try again.",
        });
      }
    } catch {
      setNotice({ tone: "err", text: "We could not cancel this subscription. Please try again." });
    } finally {
      setCancelling(false);
    }
  };

  const changeManualPause = async (plan, resumeOn) => {
    setPausing(true);
    setNotice(null);
    const res = await setManualSubscriptionPause({
      memberId: member?.community_member_id,
      memberEmail: member?.email,
      productId: plan.product_id,
      subscriptionId: plan.subscription_id,
      resumeAt: resumeOn,
      jwt: member?.member_token,
    });
    setPausing(false);
    if (res.success) {
      setNotice({ tone: "ok", text: res.message || (resumeOn ? "Subscription paused." : "Subscription resumed.") });
      setPauseTarget(null);
      setResumeAt("");
      await load(undefined, true);
    } else {
      setNotice({ tone: "err", text: res.message || "We could not update this subscription. Please try again." });
    }
  };

  /*
   * Only a live subscription can be cancelled — a cancelled/expired one keeps
   * its badge but loses the button.
   *
   * The DATE is the authority, not the status string. The status list below
   * cannot be exhaustive (a `paused` plan passed it, and so would any value the
   * gateway adds later), so a plan whose paid-up window had closed could still
   * be shown green with a "Cancel plan" button while the expiry gate was
   * simultaneously telling the same member their access had ended. `expires_at`
   * and the API's own enforcement both go by the date; this now agrees with them.
   */
  const isPaused = (p) => String(p.subscription_status || "").toLowerCase() === "paused";

  const isCancellable = (p) =>
    p.is_subscription &&
    p.subscription_id &&
    (!planEnded(p) || isPaused(p)) &&
    !["cancelled", "canceled", "expired", "inactive"].includes(
      String(p.subscription_status || "").toLowerCase(),
    );

  // Date handling lives in subscriptionGate.js — the full-screen gate decides
  // "has this run out?" from the same helpers, and two copies of that rule
  // would eventually disagree about whether a plan is still live.
  const planDate = formatPlanDate;
  const accessEnded = planEnded;

  const money = (amt, cur) => {
    if (amt == null || amt === "") return "—";
    const n = Number(amt);
    return Number.isNaN(n) ? `${cur} ${amt}` : `${cur} ${n.toFixed(2)}`;
  };

  const planPrice = (p) => {
    const sub = p.subscription_plan;
    return p.is_subscription && sub?.amount != null && sub.amount !== ""
      ? money(sub.amount, sub.currency || p.currency)
      : money(p.price, p.currency);
  };

  /* ── Presentation helpers ──────────────────────────────────────────────── */

  // A live subscription reads green; one that has been stopped reads neutral
  // (or amber once its paid-up window has actually closed), so the two are
  // distinguishable at a glance rather than by reading the word.
  const statusTone = (p) => {
    if (isCancellable(p)) {
      return "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200/70 dark:ring-emerald-500/30";
    }
    if (accessEnded(p)) {
      return "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200/70 dark:ring-amber-500/30";
    }
    return "bg-gray-100 text-gray-500 ring-1 ring-gray-200/70";
  };

  // `payment_type` on the order row is a NUMBER (1 = paid, 0 = manual/free), so
  // rendering it raw put a bare "1" on every card. The gateway is what a member
  // actually recognises, so prefer that and drop the number entirely.
  const payLabel = (p) => {
    const gateway = p.subscription_plan?.gateway_type;
    if (gateway) return String(gateway).replace(/\b\w/g, (c) => c.toUpperCase());
    const raw = String(p.payment_type ?? "").trim();
    return raw && !/^\d+$/.test(raw) ? raw : "";
  };

  // The platform's frequency vocabulary carries the parent's long-standing typo
  // ("quaterly"), which is fine in storage but should not reach a buyer's eye.
  const FREQUENCY_LABELS = {
    quaterly: "Quarterly",
    quarterly: "Quarterly",
    monthly: "Monthly",
    yearly: "Yearly",
    weekly: "Weekly",
    daily: "Daily",
    month: "Monthly",
    year: "Yearly",
    week: "Weekly",
    day: "Daily",
  };

  const cycleLabel = (p) => {
    const name = p.subscription_plan?.billing_name;
    if (!name) return "";
    const tail = String(name).split("/").pop().trim().toLowerCase();
    return FREQUENCY_LABELS[tail] || tail;
  };

  const liveCount = (plans || []).filter(isCancellable).length;

  const subtitle = () => {
    if (plans === null || error) return "Your plans and subscriptions";
    if (plans.length === 0) return "Your plans and subscriptions";
    const parts = [];
    if (liveCount) parts.push(`${liveCount} active subscription${liveCount === 1 ? "" : "s"}`);
    const rest = plans.length - liveCount;
    if (rest) parts.push(`${rest} other plan${rest === 1 ? "" : "s"}`);
    return parts.join(" · ");
  };

  /** One meta chip — icon + text, muted. */
  const Meta = ({ icon, children }) =>
    children ? (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
        {icon("w-3.5 h-3.5 shrink-0 opacity-70")}
        <span className="truncate">{children}</span>
      </span>
    ) : null;

  /* ── Body ──────────────────────────────────────────────────────────────── */

  const body = (
    <>
      {notice && (
        <div
          className={`mb-4 rounded-xl px-3.5 py-2.5 text-[12px] font-medium ring-1 ${notice.tone === "ok"
            ? "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-100 dark:ring-emerald-500/30"
            : "bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-300 ring-red-100 dark:ring-red-500/30"
            }`}
        >
          {notice.text}
        </div>
      )}

      {plans === null && !error ? (
        <InlineLoader className="justify-center py-12" label="Loading your plans…" primary={primary} />
      ) : error ? (
        <p className="py-12 text-center text-sm text-red-500">Couldn't load your plans. Please try again.</p>
      ) : plans.length === 0 ? (
        <EmptyState
          compact
          art="plans"
          accent={primary}
          title="No plans yet"
          message="Any paid plan or subscription you take in this membership is listed here, with what it renews to and when."
        />
      ) : (
        <ul className="space-y-3.5">
          {plans.map((p) => {
            const live = isCancellable(p);
            const ended = accessEnded(p);
            return (
              <li
                key={p.id}
                className="group relative overflow-hidden rounded-2xl bg-surface ring-1 ring-gray-100 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-lg"
              >
                {/* Brand accent rail — solid for a live plan, faded once it is not. */}
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 w-[3px]"
                  style={{
                    background: live
                      ? `linear-gradient(180deg, ${primary} 0%, ${shade(primary, -22)} 100%)`
                      : `${primary}33`,
                  }}
                />
                {/* Whisper of brand tint, so the card reads as part of the theme. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 opacity-60"
                  style={{ background: `linear-gradient(135deg, ${primary}0A 0%, transparent 55%)` }}
                />

                <div className="relative p-4 pl-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="truncate text-[15px] font-bold leading-tight text-gray-900">
                          {p.title}
                        </h4>
                        {p.is_subscription && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusTone(p)}`}>
                            {planStatusLabel(p)}
                          </span>
                        )}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <Meta icon={Icon.calendar}>{planDate(p.created_at)}</Meta>
                        <Meta icon={Icon.wallet}>{payLabel(p)}</Meta>
                        <Meta icon={Icon.receipt}>
                          {p.invoice_id && Number(p.price) > 0 ? `#${p.invoice_id}` : ""}
                        </Meta>
                      </div>
                    </div>

                    {/* Price pill — the one loud element on the card. */}
                    <div className="shrink-0 text-right">
                      <span
                        className="inline-block rounded-xl px-3 py-1.5 text-[15px] font-extrabold leading-none ring-1"
                        style={{
                          backgroundColor: `${primary}14`,
                          color: primary,
                          borderColor: "transparent",
                          boxShadow: `inset 0 0 0 1px ${primary}26`,
                        }}
                      >
                        {planPrice(p)}
                      </span>
                      {cycleLabel(p) && (
                        <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                          {cycleLabel(p)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Renewal / access strip — the answer to "when does this end?" */}
                  {(() => {
                    const trialEnds = live ? planTrialEndsAt(p) : null;
                    const renews = p.subscription_plan?.next_billing_at && live;
                    const window = p.is_subscription && !live && planAccessUntil(p);
                    if (!trialEnds && !renews && !window) return null;
                    return (
                      <div
                        className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2"
                        style={{
                          backgroundColor: renews ? `${primary}0D` : "transparent",
                          boxShadow: renews ? "none" : "inset 0 0 0 1px rgb(var(--c-gray-200) / .7)",
                        }}
                      >
                        <span style={{ color: renews ? primary : undefined }} className={renews ? "" : "text-gray-400"}>
                          {renews ? Icon.renew("w-4 h-4") : Icon.clock("w-4 h-4")}
                        </span>
                        <p className="text-[11.5px] font-semibold text-gray-600">
                          {trialEnds
                            ? `Trial ends ${planDate(trialEnds)} — then ${planPrice(p)}${cycleLabel(p) ? ` / ${cycleLabel(p)}` : ""}`
                            : renews
                            ? `${isManualPlan(p) ? "Next payment due" : "Renews"} ${planDate(p.subscription_plan.next_billing_at)}`
                            : ended
                              ? `Access ended ${planDate(planAccessUntil(p))}`
                              : `You keep access until ${planDate(planAccessUntil(p))}`}
                        </p>
                      </div>
                    );
                  })()}

                  {p.bumps?.length > 0 && (
                    <p className="mt-2 text-[11px] text-gray-500">
                      <span className="font-semibold text-gray-600">Includes:</span>{" "}
                      {p.bumps.map((b) => `${b.name} (${money(b.price, p.currency)})`).join(", ")}
                    </p>
                  )}

                  {isManualPlan(p) && (() => {
                    const sub = p.subscription_plan || {};
                    const manualStatus = String(p.subscription_status || "").toLowerCase();
                    const payUrl = planRenewUrl(p);
                    return (
                      <div className="mt-3 space-y-2">
                        {manualStatus === "paused" && sub.pause_until && (
                          <p className="text-[11.5px] font-semibold text-gray-600">
                            Paused until {planDate(sub.pause_until)}
                          </p>
                        )}
                        {sub.payment_link_expired && (
                          <p className="rounded-xl bg-amber-50 dark:bg-amber-500/15 px-3 py-2 text-[11.5px] font-medium text-amber-700 dark:text-amber-300">
                            Your payment link has expired. Please ask the seller to send a new one.
                          </p>
                        )}
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {sub.can_pay_now && sub.pay_now_url && payUrl && (
                            <a
                              href={payUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-xl px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:opacity-95"
                              style={{ background: `linear-gradient(135deg, ${primary} 0%, ${shade(primary, -18)} 100%)` }}
                            >
                              Pay now
                            </a>
                          )}
                          {manualStatus === "active" && live && (
                            <button
                              type="button"
                              disabled={pausing}
                              onClick={() => { setPauseTarget(p); setResumeAt(""); setNotice(null); }}
                              className="rounded-xl px-3 py-1.5 text-[12px] font-semibold text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-100 disabled:opacity-50"
                            >
                              Pause
                            </button>
                          )}
                          {manualStatus === "paused" && (
                            <button
                              type="button"
                              disabled={pausing}
                              onClick={() => changeManualPause(p, "")}
                              className="rounded-xl px-3 py-1.5 text-[12px] font-semibold text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-100 disabled:opacity-50"
                            >
                              {pausing ? "Resuming…" : "Resume"}
                            </button>
                          )}
                        </div>
                        {pauseTarget?.id === p.id && (
                          <div className="rounded-xl border border-gray-200 bg-surface p-3">
                            <p className="text-[12px] font-bold text-gray-800">Pause “{p.title}”?</p>
                            <p className="mt-0.5 text-[11px] text-gray-500">
                              Your access is paused until the date you pick, up to 90 days. You can resume earlier.
                            </p>
                            <input
                              type="date"
                              min={isoDayFromNow(1)}
                              max={isoDayFromNow(90)}
                              value={resumeAt}
                              onChange={(e) => setResumeAt(e.target.value)}
                              aria-label="Resume on"
                              className="mt-2 rounded-xl border border-gray-200 bg-surface px-3 py-1.5 text-[12px] text-gray-800 outline-none"
                            />
                            <div className="mt-2 flex justify-end gap-2">
                              <button
                                type="button"
                                disabled={pausing}
                                onClick={() => { setPauseTarget(null); setResumeAt(""); }}
                                className="rounded-xl px-3 py-1.5 text-[12px] font-semibold text-gray-500 transition hover:bg-gray-100 disabled:opacity-50"
                              >
                                Keep active
                              </button>
                              <button
                                type="button"
                                disabled={pausing || !resumeAt}
                                onClick={() => changeManualPause(p, resumeAt)}
                                className="rounded-xl px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:opacity-95 disabled:opacity-50"
                                style={{ background: primary }}
                              >
                                {pausing ? "Pausing…" : "Pause subscription"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {live && (
                    <div className="mt-3 flex justify-end border-t border-gray-100 pt-2.5">
                      <button
                        type="button"
                        onClick={() => { setCancelTarget(p); setCancelReason(""); setNotice(null); }}
                        className="rounded-lg px-2.5 py-1 text-[11.5px] font-semibold text-gray-400 transition hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/15 dark:hover:text-red-300"
                      >
                        Cancel plan
                      </button>
                    </div>
                  )}

                  {/* Inline confirm — a cancellation is irreversible, so it asks
                      first and lets the member say why (the API accepts a reason). */}
                  {cancelTarget?.id === p.id && (
                    <div className="mt-3 rounded-xl border border-red-100 dark:border-red-500/30 bg-red-50/60 dark:bg-red-500/10 p-3">
                      <p className="text-[12px] font-bold text-gray-800">
                        Cancel “{p.title}”?
                      </p>
                      <p className="mt-0.5 text-[11px] text-gray-500">
                        {planAccessUntil(p)
                          ? `You'll keep access until ${planDate(planAccessUntil(p))}. This can't be undone.`
                          : "You'll keep access until the current period ends. This can't be undone."}
                      </p>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={2}
                        maxLength={500}
                        placeholder="Reason (optional)"
                        className="mt-2 w-full rounded-xl border border-gray-200 bg-surface px-3 py-2 text-[12px] text-gray-800 outline-none transition focus:border-red-300 dark:focus:border-red-500/30"
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <button
                          type="button"
                          disabled={cancelling}
                          onClick={() => { setCancelTarget(null); setCancelReason(""); }}
                          className="rounded-xl px-3 py-1.5 text-[12px] font-semibold text-gray-500 transition hover:bg-gray-100 disabled:opacity-50"
                        >
                          Keep plan
                        </button>
                        <button
                          type="button"
                          disabled={cancelling}
                          onClick={confirmCancel}
                          className="rounded-xl bg-red-500 px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-red-600 disabled:opacity-50"
                        >
                          {cancelling ? "Cancelling…" : "Cancel subscription"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );

  /* ── Embedded (Spotlight "My Plans" page): a card, not a popup ──────────── */

  if (embedded) {
    return (
      <div className="w-full overflow-hidden rounded-2xl bg-surface ring-1 ring-gray-200 shadow-sm">
        <div
          className="flex items-center gap-3 border-b border-gray-100 px-5 py-4"
          style={{ background: `linear-gradient(135deg, ${primary}14 0%, transparent 70%)` }}
        >
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface shadow-sm ring-1 ring-gray-100"
            style={{ color: primary }}
          >
            {Icon.wallet("w-5 h-5")}
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-bold leading-tight text-gray-900">My Plans</h2>
            <p className="truncate text-[11.5px] text-gray-500">{subtitle()}</p>
          </div>
        </div>
        <div className="p-5">{body}</div>
      </div>
    );
  }

  /* ── Popup ─────────────────────────────────────────────────────────────── */

  // No backdrop-click close — closing is via the X / Close only, so a stray
  // click on the dim area cannot dismiss the plans list mid-read.
  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-center bg-ink/60 p-0 backdrop-blur-md sm:items-center sm:p-4">
      <style>{`
        @keyframes plansPop { from { opacity: 0; transform: translateY(16px) scale(.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
      <div
        className="relative flex h-full max-h-none w-full flex-col overflow-hidden rounded-none bg-surface shadow-2xl animate-[plansPop_.28s_cubic-bezier(.22,1,.36,1)] sm:h-auto sm:max-h-[92vh] sm:max-w-lg sm:rounded-3xl sm:ring-1 sm:ring-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Branded header ── */}
        <div className="relative shrink-0 overflow-hidden bg-ink px-6 pb-6 pt-7 text-center">
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{ background: `linear-gradient(160deg, ${primary}4D 0%, ${primary}C2 100%)` }}
          />
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3.5 top-3.5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition hover:bg-white/35"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="h-4 w-4">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div
            className="relative z-10 mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface shadow-md"
            style={{ color: primary }}
          >
            {Icon.wallet("w-7 h-7")}
          </div>
          <h3 className="relative z-10 mt-3 text-lg font-bold text-white" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>
            My Plans
          </h3>
          <p className="relative z-10 mt-0.5 text-sm text-white/95" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>
            {subtitle()}
          </p>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto scrollbar-hide bg-gray-50/50 px-5 py-5 sm:px-6">
          {body}
        </div>

        {/* ── Footer ── */}
        <div className="flex shrink-0 items-center justify-end border-t border-gray-100 bg-surface px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95"
            style={{ background: `linear-gradient(135deg, ${primary} 0%, ${shade(primary, -18)} 100%)` }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function isoDayFromNow(offsetDays) {
  const day = new Date();
  day.setDate(day.getDate() + offsetDays);
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
}
